import { useEffect, useState } from "react";

/* ──────────────────────────────────────────────────────────────────────────
   Dark or light, per person, remembered on this device.

   Dark is the default. A few accounts start light because the people who use
   them asked for it (adminoga, 2026-10-05); anyone can switch from the
   account menu, and their choice beats the default. The colours themselves
   are in @signet/tokens: tokens.css and light.mjs.
   ────────────────────────────────────────────────────────────────────────── */

export type Theme = "dark" | "light";

const LIGHT_BY_DEFAULT = new Set(["adminoga"]);
// The last theme drawn on this device, so the sign-in page and the first
// paint before /api/me answers do not flash the other one.
const LAST = "signet:theme:last";
const keyFor = (username: string) => `signet:theme:${username.toLowerCase()}`;

const read = (key: string): Theme | null => {
  try {
    const value = localStorage.getItem(key);
    return value === "dark" || value === "light" ? value : null;
  } catch {
    return null;
  }
};
const write = (key: string, theme: Theme) => {
  try {
    localStorage.setItem(key, theme);
  } catch {
    // Private windows can refuse storage; the theme still applies for this visit.
  }
};

export function themeFor(username: string): Theme {
  return read(keyFor(username)) ?? (LIGHT_BY_DEFAULT.has(username.toLowerCase()) ? "light" : "dark");
}

const listeners = new Set<(theme: Theme) => void>();
let current: Theme = "dark";

export function applyTheme(theme: Theme): void {
  current = theme;
  const root = document.documentElement;
  if (theme === "light") root.dataset.theme = "light";
  else delete root.dataset.theme;
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", theme === "light" ? "#fbfbfe" : "#0b0d0f");
  write(LAST, theme);
  for (const listener of listeners) listener(theme);
}

/** Before React renders: whatever this device drew last. */
export function applyLastTheme(): void {
  applyTheme(read(LAST) ?? "dark");
}

/** Remembers the choice for this person and draws it. */
export function setTheme(username: string, theme: Theme): void {
  write(keyFor(username), theme);
  applyTheme(theme);
}

export function useTheme(): Theme {
  const [theme, set] = useState(current);
  useEffect(() => {
    listeners.add(set);
    set(current);
    return () => void listeners.delete(set);
  }, []);
  return theme;
}
